import React, { useCallback, useState } from "react";
import { Card } from "@shadcn-in-nx/ui/card";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Paperclip, Loader2 } from "lucide-react";
import { format } from "date-fns";
import DOMPurify from "dompurify";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { toast } from "sonner";

export default function TicketMessageList({ messages }) {
    const { t } = useTranslation();
    const { call: getSignedUrl } = useBluelagoon({ method: "getAttachmentSignedUrl" });
    const [downloadingId, setDownloadingId] = useState(null);

    const handleAttachmentClick = useCallback(async (e, att, idx) => {
        e.preventDefault();
        const id = `${att.url}-${idx}`;
        setDownloadingId(id);
        try {
            const res = await getSignedUrl({ fileUrl: att.url });
            if (res?.data?.signedUrl) {
                window.open(res.data.signedUrl, "_blank", "noopener,noreferrer");
            } else {
                toast.error(t("admin.support.downloadError", "Failed to download file"));
            }
        } catch {
            toast.error(t("admin.support.downloadError", "Failed to download file"));
        } finally {
            setDownloadingId(null);
        }
    }, [getSignedUrl, t]);

    return (
        <div className="space-y-3 sm:space-y-4">
            <h2 className="font-semibold">{t("admin.support.messages", "Messages")}</h2>
            {messages.map((message) => (
                <Card key={message.id} className={`p-3 sm:p-4 ${message.isFromStaff ? "ml-2 sm:ml-8 bg-blue-50 border-blue-200" : "mr-2 sm:mr-8"}`}>
                    <div className="flex items-center justify-between mb-2 gap-2">
                        <span className="font-medium text-sm truncate">
                            {message.isFromStaff ? (
                                <Badge variant="secondary">{t("admin.support.staff", "Staff")}</Badge>
                            ) : (
                                message.sender?.firstName || message.senderName || message.senderEmail || t("admin.support.customer", "Customer")
                            )}
                        </span>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {format(new Date(message.createdAt), "MMM d, h:mm a")}
                        </span>
                    </div>
                    <div className="text-sm break-words overflow-hidden" dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                            /<[a-z][\s\S]*>/i.test(message.content)
                                ? message.content
                                : `<p style="white-space:pre-wrap">${message.content}</p>`
                        ),
                    }} />
                    {message.attachments?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                            {message.attachments.map((att, idx) => {
                                const id = `${att.url}-${idx}`;
                                const isDownloading = downloadingId === id;
                                return (
                                    <button key={idx} onClick={(e) => handleAttachmentClick(e, att, idx)} disabled={isDownloading}
                                        className="flex items-center gap-1 text-xs text-blue-600 hover:underline bg-blue-50 px-2 py-1 rounded cursor-pointer disabled:opacity-50">
                                        {isDownloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />}
                                        {isDownloading ? t("admin.support.downloadingFile", "Downloading...") : (att.name || "Attachment")}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </Card>
            ))}
        </div>
    );
}
